import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { bookingsApi, vesselsApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Container, 
  Ship,
  Package,
  Check,
  Plus,
  X,
  Loader2,
  AlertCircle,
  ArrowRight,
  Clock
} from 'lucide-react';
import { formatDate, formatCurrency, formatDateTime } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../services/api';
import { EtaDialog } from '../../components/bridge/EtaDialog';
import { EventTimeline } from '../../components/bridge/EventTimeline';
import { OccurredAtField } from '../../components/bridge/OccurredAtField';
import { CONTAINER_OPERATIONS } from '../../components/bridge/operations';
import { EtaDisplay } from '../../components/shared/EtaDisplay';
import { FreeTimeCard } from '../../components/shared/FreeTimeCard';

function AgentContainers() {
  const [searchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [containerNumbers, setContainerNumbers] = useState(['']);
  const [vesselName, setVesselName] = useState('');
  const [voyageNumber, setVoyageNumber] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [activeTab, setActiveTab] = useState('assign');
  
  // Operation state
  const [selectedOperation, setSelectedOperation] = useState('');
  const [operationLocation, setOperationLocation] = useState('');
  const [operationNotes, setOperationNotes] = useState('');
  const [operationOccurredAt, setOperationOccurredAt] = useState('');
  const [operationSearch, setOperationSearch] = useState('');
  const [performingOperation, setPerformingOperation] = useState(false);
  const [etaDialogOpen, setEtaDialogOpen] = useState(false);

  useEffect(function() {
    loadData();
  }, []);

  useEffect(function() {
    const bookingId = searchParams.get('booking');
    if (bookingId && bookings.length > 0) {
      const booking = bookings.find(function(b) { return b.id === bookingId; });
      if (booking) {
        handleSelectBooking(booking);
      }
    }
  }, [bookings, searchParams]);

  function handleSelectBooking(booking) {
    setSelectedBooking(booking);
    
    // Generate container numbers
    const newContainers = [];
    for (let i = 0; i < booking.container_count; i++) {
      newContainers.push(generateContainerNumber());
    }
    setContainerNumbers(newContainers);
    
    // Pre-fill vessel info from booking's schedule (if available)
    if (booking.vessel_name) {
      setVesselName(booking.vessel_name);
    } else {
      setVesselName('');
    }
    if (booking.voyage_number) {
      setVoyageNumber(booking.voyage_number);
    } else {
      setVoyageNumber('');
    }
  }

  async function loadData() {
    try {
      const [bookingsRes, vesselsRes] = await Promise.all([
        bookingsApi.getAll(),
        vesselsApi.getAll()
      ]);
      
      setAllBookings(bookingsRes.data);
      
      // Bookings needing container assignment
      const needsAssignment = bookingsRes.data.filter(function(b) {
        return b.status === 'pending' && (!b.container_numbers || b.container_numbers.length === 0);
      });
      setBookings(needsAssignment);
      setVessels(vesselsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }

  // ISO 6346: SDVU + 6-digit serial + check digit (letters A-Z map to 10-38 skipping multiples of 11)
  function iso6346CheckDigit(body) {
    const letters = [10, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 34, 35, 36, 37, 38];
    let total = 0;
    for (let i = 0; i < body.length; i++) {
      const ch = body[i];
      const v = /[A-Z]/.test(ch) ? letters[ch.charCodeAt(0) - 65] : parseInt(ch, 10);
      total += v * Math.pow(2, i);
    }
    return (total % 11) % 10;
  }

  function generateContainerNumber() {
    const body = 'SDVU' + Math.floor(100000 + Math.random() * 900000).toString();
    return body + iso6346CheckDigit(body);
  }

  function isValidContainerNumber(n) {
    const c = (n || '').replace(/[\s-]/g, '').toUpperCase();
    return /^[A-Z]{3}[UJZ]\d{7}$/.test(c) && iso6346CheckDigit(c.slice(0, 10)) === parseInt(c[10], 10);
  }

  function addContainer() {
    setContainerNumbers([...containerNumbers, generateContainerNumber()]);
  }

  function removeContainer(index) {
    if (containerNumbers.length > 1) {
      const newContainers = containerNumbers.filter(function(_, i) { return i !== index; });
      setContainerNumbers(newContainers);
    }
  }

  function updateContainer(index, value) {
    const newContainers = [...containerNumbers];
    newContainers[index] = value.toUpperCase();
    setContainerNumbers(newContainers);
  }

  async function handleAssign() {
    if (!selectedBooking) {
      toast.error('Please select a booking');
      return;
    }
    if (!vesselName || !voyageNumber) {
      toast.error('Please select vessel and enter voyage number');
      return;
    }
    if (containerNumbers.some(function(c) { return !c.trim(); })) {
      toast.error('Please fill all container numbers');
      return;
    }
    if (containerNumbers.some(function(c) { return !isValidContainerNumber(c); })) {
      toast.error('One or more container numbers fail the ISO 6346 check digit');
      return;
    }

    setAssigning(true);
    try {
      const res = await api.post('/bookings/' + selectedBooking.id + '/assign-containers', {
        booking_id: selectedBooking.id,
        container_numbers: containerNumbers.map(function(c) { return c.replace(/[\s-]/g, '').toUpperCase(); }),
        vessel_name: vesselName,
        voyage_number: voyageNumber
      });
      const seals = (res.data.container_records || []).map(function(c) { return c.number + ' / seal ' + c.seal_number; });
      toast.success('Containers assigned' + (res.data.bl_number ? ' · B/L ' + res.data.bl_number : ''), { description: seals.join('  ·  ') });
      setSelectedBooking(null);
      setContainerNumbers(['']);
      setVesselName('');
      setVoyageNumber('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign containers');
    } finally {
      setAssigning(false);
    }
  }

  async function handleOperation() {
    if (!selectedBooking) {
      toast.error('Please select a booking');
      return;
    }
    if (!selectedOperation) {
      toast.error('Please select an operation');
      return;
    }

    setPerformingOperation(true);
    try {
      const response = await api.post('/bookings/' + selectedBooking.id + '/container-operation', {
        operation_type: selectedOperation,
        location: operationLocation || null,
        notes: operationNotes || null,
        occurred_at: operationOccurredAt || null
      });
      toast.success(response.data.message + ' — ' + formatDateTime(response.data.event.timestamp));
      setSelectedOperation('');
      setOperationLocation('');
      setOperationNotes('');
      setOperationOccurredAt('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    } finally {
      setPerformingOperation(false);
    }
  }

  // Bookings with containers assigned (for operations) — delivered ones last so empties can still be returned
  const opsSearch = operationSearch.trim().toLowerCase();
  const assignedBookings = allBookings.filter(function(b) {
    if (!(b.container_numbers && b.container_numbers.length > 0)) return false;
    if (!opsSearch) return true;
    return b.booking_number.toLowerCase().includes(opsSearch) || (b.bl_number || '').toLowerCase().includes(opsSearch) || b.container_numbers.some(function (c) { return c.toLowerCase().includes(opsSearch); });
  }).sort(function (a, b) { return (a.status === 'delivered') - (b.status === 'delivered'); });

  return (
    <div className="space-y-8" data-testid="agent-containers">
      <div>
        <h1 className="text-[32px] sv-display text-ink">Container operations</h1>
        <p className="text-gray-600 mt-1">Assign containers and manage shipment lifecycle</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
          <TabsTrigger value="assign" className="flex items-center gap-2">
            <Container className="h-4 w-4" /> Assign Containers
          </TabsTrigger>
          <TabsTrigger value="operations" className="flex items-center gap-2">
            <Ship className="h-4 w-4" /> Operations
          </TabsTrigger>
        </TabsList>

        {/* Assign Containers Tab */}
        <TabsContent value="assign">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Booking Selection */}
            <div className="bg-white rounded-sm shadow-card p-6">
              <h2 className="text-lg font-medium text-ink mb-4 flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Pending Bookings ({bookings.length})
              </h2>
              
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : bookings.length > 0 ? (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {bookings.map(function(booking) {
                    const isSelected = selectedBooking?.id === booking.id;
                    const hasSchedule = booking.vessel_name && booking.voyage_number;
                    return (
                      <div 
                        key={booking.id}
                        onClick={function() { handleSelectBooking(booking); }}
                        className={"p-4 rounded-sm border-2 cursor-pointer transition-all " + (isSelected ? 'border-blue-500 bg-blue-500/5' : 'border-gray-100 hover:border-gray-200')}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="sv-mono font-medium text-ink">{booking.booking_number}</p>
                            <p className="text-sm text-gray-500">
                              {booking.origin_port.split(',')[0]} → {booking.destination_port.split(',')[0]}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {booking.container_count}x {booking.container_type} • {formatCurrency(booking.price)}
                            </p>
                            {hasSchedule && (
                              <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                                <Ship className="h-3 w-3" />
                                {booking.vessel_name} • {booking.voyage_number}
                              </p>
                            )}
                          </div>
                          {isSelected && (
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                              <Check className="h-5 w-5 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Check className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-500">All bookings have containers assigned!</p>
                </div>
              )}
            </div>

            {/* Container Assignment Form */}
            <div className="bg-white rounded-sm shadow-card p-6">
              <h2 className="text-lg font-medium text-ink mb-4 flex items-center gap-2">
                <Container className="h-5 w-5 text-blue-600" />
                Assign Containers
              </h2>

              {selectedBooking ? (
                <div className="space-y-6">
                  <div className="p-4 bg-gray-50 rounded-sm">
                    <p className="text-sm text-gray-500">Selected booking</p>
                    <p className="sv-mono font-medium text-ink">{selectedBooking.booking_number}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedBooking.origin_port.split(',')[0]} → {selectedBooking.destination_port.split(',')[0]}
                    </p>
                    <p className="text-sm text-gray-600">
                      Requires {selectedBooking.container_count} container(s)
                    </p>
                    {selectedBooking.vessel_name && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Assigned schedule</p>
                        <div className="flex items-center gap-2 text-blue-600">
                          <Ship className="h-4 w-4" />
                          <span className="font-medium">{selectedBooking.vessel_name}</span>
                          <span className="text-gray-400">•</span>
                          <span className="sv-mono">{selectedBooking.voyage_number}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Vessel Selection - Pre-filled from schedule if available */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="font-medium text-gray-700">Vessel</Label>
                      {selectedBooking?.vessel_name && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          From Schedule
                        </Badge>
                      )}
                    </div>
                    {selectedBooking?.vessel_name && (
                      <p className="text-xs text-gray-500 -mt-1 mb-2">
                        Pre-filled from booking schedule. Override below for private shipments.
                      </p>
                    )}
                    <Select value={vesselName} onValueChange={setVesselName}>
                      <SelectTrigger className="h-12 rounded-sm">
                        <SelectValue placeholder="Select vessel" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Show pre-selected vessel first if it's a custom one not in the list */}
                        {vesselName && !vessels.some(function(v) { return v.name === vesselName; }) && (
                          <SelectItem value={vesselName}>
                            <div className="flex items-center gap-2">
                              <Ship className="h-4 w-4" />
                              {vesselName} (From Schedule)
                            </div>
                          </SelectItem>
                        )}
                        {vessels.filter(function(v) { return v.status === 'active'; }).map(function(vessel) {
                          return (
                            <SelectItem key={vessel.id} value={vessel.name}>
                              <div className="flex items-center gap-2">
                                <Ship className="h-4 w-4" />
                                {vessel.name} ({vessel.capacity_teu.toLocaleString()} TEU)
                              </div>
                            </SelectItem>
                          );
                        })}
                        <SelectItem value="__custom__">
                          <div className="flex items-center gap-2 text-blue-600">
                            <Plus className="h-4 w-4" />
                            Enter Custom Vessel...
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {vesselName === '__custom__' && (
                      <Input
                        placeholder="Enter custom vessel name"
                        onChange={function(e) { setVesselName(e.target.value); }}
                        className="h-12 rounded-sm mt-2"
                        autoFocus
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="font-medium text-gray-700">Voyage number</Label>
                      {selectedBooking?.voyage_number && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          From Schedule
                        </Badge>
                      )}
                    </div>
                    <Input
                      placeholder="e.g., 251EA"
                      value={voyageNumber}
                      onChange={function(e) { setVoyageNumber(e.target.value.toUpperCase()); }}
                      className="h-12 rounded-sm sv-mono"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="font-medium text-gray-700">Container numbers</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addContainer} className="rounded-sm">
                        <Plus className="h-4 w-4 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {containerNumbers.map(function(container, index) {
                        return (
                          <div key={index} className="flex items-center gap-2">
                            <div className="flex-1 relative">
                              <Input
                                placeholder="SDVU0000000"
                                value={container}
                                onChange={function(e) { updateContainer(index, e.target.value.toUpperCase()); }}
                                className={"h-12 rounded-sm sv-mono " + (container && !isValidContainerNumber(container) ? 'border-error-700 focus-visible:ring-error-700' : '')}
                                data-testid={"container-number-" + index}
                              />
                              {container && !isValidContainerNumber(container) && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-error-700" data-testid={"container-number-invalid-" + index}>invalid check digit</span>}
                            </div>
                            {containerNumbers.length > 1 && (
                              <Button type="button" variant="outline" size="icon" onClick={function() { removeContainer(index); }} className="h-12 w-12 rounded-sm text-red-500 hover:bg-red-50">
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <Button onClick={handleAssign} disabled={assigning} className="w-full h-14 bg-ink hover:bg-ink-800 rounded-sm font-medium text-lg" data-testid="assign-submit">
                    {assigning ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Assigning...</>
                    ) : (
                      <><Check className="mr-2 h-5 w-5" /> Assign selected containers</>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Select a booking to assign containers</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Operations Tab */}
        <TabsContent value="operations">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Booking Selection for Operations */}
            <div className="bg-white rounded-sm shadow-card p-6">
              <h2 className="text-lg font-medium text-ink mb-4 flex items-center gap-2">
                <Ship className="h-5 w-5 text-blue-600" />
                Active Shipments ({assignedBookings.length})
              </h2>
              <Input value={operationSearch} onChange={function (e) { setOperationSearch(e.target.value); }} placeholder="Find by booking, B/L or container number" className="h-10 rounded-sm sv-mono mb-3" data-testid="ops-search" />
              
              {assignedBookings.length > 0 ? (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {assignedBookings.map(function(booking) {
                    const isSelected = selectedBooking?.id === booking.id;
                    return (
                      <div 
                        key={booking.id}
                        onClick={function() { setSelectedBooking(booking); }}
                        className={"p-4 rounded-sm border-2 cursor-pointer transition-all " + (isSelected ? 'border-blue-500 bg-blue-500/5' : 'border-gray-100 hover:border-gray-200')}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="sv-mono font-medium text-ink">{booking.booking_number}</p>
                          <Badge className={"status-badge status-" + booking.status}>
                            {booking.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500 mb-1">
                          {booking.origin_port.split(',')[0]} → {booking.destination_port.split(',')[0]}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {booking.container_numbers?.map(function(cn, i) {
                            const rec = (booking.containers || []).find(function(c) { return c.number === cn; });
                            return (
                              <span key={i} className={"text-xs sv-mono px-2 py-1 rounded " + (rec && rec.vgm_kg ? 'bg-success-50 text-success-700' : 'bg-amber-50 text-amber-800')} title={rec ? rec.iso_code + ' · tare ' + rec.tare_kg + ' kg' + (rec.vgm_kg ? ' · VGM ' + rec.vgm_kg + ' kg (' + rec.vgm_method + ')' : ' · VGM pending') : ''} data-testid={"container-chip-" + cn}>
                                {cn}{rec && rec.seal_number && <span className="opacity-70"> · seal {rec.seal_number}</span>}<span className="opacity-70"> · {rec && rec.vgm_kg ? 'VGM ' + Number(rec.vgm_kg).toLocaleString() + ' kg' : 'VGM pending'}</span>
                              </span>
                            );
                          })}
                        </div>
                        {booking.vessel_name && (
                          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                            <Ship className="h-3 w-3" /> {booking.vessel_name} • {booking.voyage_number}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No active shipments</p>
                </div>
              )}
            </div>

            {/* Operations Panel */}
            <div className="bg-white rounded-sm shadow-card p-6">
              <h2 className="text-lg font-medium text-ink mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                Update Status
              </h2>

              {selectedBooking && selectedBooking.container_numbers?.length > 0 ? (
                <div className="space-y-6">
                  <div className="p-4 bg-gray-50 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <p className="sv-mono font-medium text-ink">{selectedBooking.booking_number}</p>
                      <Badge className={"status-badge status-" + selectedBooking.status}>
                        {selectedBooking.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 sv-mono" data-testid="ops-containers">
                      {(selectedBooking.containers && selectedBooking.containers.length ? selectedBooking.containers.map(function(c) { return c.number + (c.seal_number ? ' (seal ' + c.seal_number + ')' : ''); }) : (selectedBooking.container_numbers || [])).join(', ')}
                    </p>
                    <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-gray-200">
                      <div className="text-sm text-gray-600 flex items-center gap-2"><Clock className="h-4 w-4" /> ETA <EtaDisplay booking={selectedBooking} className="text-ink" testId="ops-eta" /></div>
                      <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={function () { setEtaDialogOpen(true); }} data-testid="ops-revise-eta-btn">Revise ETA</Button>
                    </div>
                  </div>

                  <FreeTimeCard bookingId={selectedBooking.id} className="border border-gray-100 shadow-none" testId="ops-free-time" />

                  {/* Operation Selection */}
                  <div className="space-y-2">
                    <Label className="font-medium text-gray-700">Select operation</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                      {Object.entries(CONTAINER_OPERATIONS).map(function([key, op]) {
                        const Icon = op.icon;
                        const isSelected = selectedOperation === key;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={function() { setSelectedOperation(key); }}
                            data-testid={"ops-operation-" + key}
                            className={"p-3 rounded-sm border-2 text-left transition-all " + (isSelected ? 'border-blue-500 bg-blue-500/5' : 'border-gray-100 hover:border-gray-200')}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Icon className={"h-4 w-4 " + (isSelected ? 'text-blue-600' : 'text-gray-400')} />
                              <span className={"text-sm font-medium " + (isSelected ? 'text-ink' : 'text-gray-700')}>{op.label}</span>
                            </div>
                            <p className="text-xs text-gray-400">{op.description}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Location Input */}
                  <div className="space-y-2">
                    <Label className="font-medium text-gray-700">Location (Optional)</Label>
                    <Input
                      placeholder="e.g., Singapore Port, Pacific Ocean"
                      value={operationLocation}
                      onChange={function(e) { setOperationLocation(e.target.value); }}
                      className="h-12 rounded-sm"
                    />
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label className="font-medium text-gray-700">Notes (Optional)</Label>
                    <Input
                      placeholder="Additional details..."
                      value={operationNotes}
                      onChange={function(e) { setOperationNotes(e.target.value); }}
                      className="h-12 rounded-sm"
                      data-testid="ops-notes"
                    />
                  </div>

                  <OccurredAtField value={operationOccurredAt} onChange={setOperationOccurredAt} testId="ops-occurred" />

                  <Button onClick={handleOperation} disabled={performingOperation || !selectedOperation} className="w-full h-14 bg-ink hover:bg-ink-800 rounded-sm font-medium text-lg" data-testid="ops-perform-btn">
                    {performingOperation ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Processing...</>
                    ) : (
                      <><ArrowRight className="mr-2 h-5 w-5" /> Perform Operation</>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Select a shipment with containers to update status</p>
                </div>
              )}
            </div>
          </div>
          {selectedBooking && <div className="mt-6"><EventTimeline bookingId={selectedBooking.id} onChanged={loadData} /></div>}
        </TabsContent>
      </Tabs>
      <EtaDialog booking={selectedBooking} open={etaDialogOpen} onOpenChange={setEtaDialogOpen} onUpdated={function (updated) { setSelectedBooking(function (prev) { return prev ? { ...prev, ...updated } : prev; }); loadData(); }} />
    </div>
  );
}

export default AgentContainers;
